import React, { useEffect, useState } from 'react';
import './styles/App.css';
import { ethers } from "ethers";
import contractAbi from './contracts/contractABI.json';
import bscLogo from './assets/bsc-logo.svg';
import ethLogo from './assets/ethlogo.png';
import { networks } from './utils/networks';
import { teal } from '@mui/material/colors';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import {
	useMediaQuery,
	CssBaseline,
	Button,
	Paper,
	Container,
	Table,
	TableContainer,
	TableHead,
	TableRow,
	TableBody,
	TableCell,
	AppBar,
	Toolbar,
	Box,
	Typography,
	Chip,
	Avatar,
	Stack,
	InputAdornment,
	Input
} from '@mui/material';
import Faq from './components/Faq';
import Footer from './components/Footer';

const ColorButton = styled(Button)(({ theme }) => ({
	color: theme.palette.getContrastText(teal[500]),
	backgroundColor: teal[500],
	'&:hover': {
		backgroundColor: teal[700],
	},
}));


// Add the domain you will be minting
const tld = '.bean';
const CONTRACT_ADDRESS = '0xAa5e0AfB6f7827BA40e01202CD8B08c55C773634';

const App = () => {
	const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

	const theme = React.useMemo(
		() =>
			createTheme({
				palette: {
					mode: prefersDarkMode ? 'dark' : 'light',
				},
			}),
		[prefersDarkMode],
	);
	const [currentAccount, setCurrentAccount] = useState('');
	// Add some state data propertie
	const [domain, setDomain] = useState('');
	const [record, setRecord] = useState('');
	const [network, setNetwork] = useState('');
	const [editing, setEditing] = useState(false);
	const [mints, setMints] = useState([]);
	const [loading, setLoading] = useState(false);

	// Implement your connectWallet method here
	async function connectWallet() {
		try {
			const { ethereum } = window;

			if (!ethereum) {
				alert("Get MetaMask -> https://metamask.io/");
				return;
			}

			// Fancy method to request access to account.
			const accounts = await ethereum.request({ method: "eth_requestAccounts" });

			// Boom! This should print out public address once we authorize Metamask.
			console.log("Connected", accounts[0]);
			setCurrentAccount(accounts[0]);
		} catch (error) {
			console.log(error);
		}
	}

	const switchNetwork = async () => {
		if (window.ethereum) {
			try {
				// Try to switch to the Mumbai testnet
				await window.ethereum.request({
					method: 'wallet_switchEthereumChain',
					params: [{ chainId: '0x61' }], // Check networks.js for hexadecimal network ids
				});
			} catch (error) {
				// This error code means that the chain we want has not been added to MetaMask
				// In this case we ask the user to add it to their MetaMask
				if (error.code === 4902) {
					try {
						await window.ethereum.request({
							method: 'wallet_addEthereumChain',
							params: [
								{
									chainId: '0x61',
									chainName: 'BSC Testnet',
									rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545/'],
									nativeCurrency: {
										name: "BSC Testnet",
										symbol: "BNB",
										decimals: 18
									},
									blockExplorerUrls: ["https://bscscan.com"]
								},
							],
						});
					} catch (error) {
						console.log(error);
					}
				}
				console.log(error);
			}
		} else {
			// If window.ethereum is not found then MetaMask is not installed
			alert('MetaMask is not installed. Please install it to use this app: https://metamask.io/download.html');
		}
	}

	const checkIfWalletIsConnected = async () => {
		const { ethereum } = window;

		if (!ethereum) {
			console.log('Make sure you have metamask!');
			return;
		} else {
			console.log('We have the ethereum object', ethereum);
		}

		const accounts = await ethereum.request({ method: 'eth_accounts' });

		if (accounts.length !== 0) {
			const account = accounts[0];
			console.log('Found an authorized account:', account);
			setCurrentAccount(account);
		} else {
			console.log('No authorized account found');
		}

		// This is the new part, we check the user's network chain ID
		const chainId = await ethereum.request({ method: 'eth_chainId' });
		setNetwork(networks[chainId]);

		ethereum.on('chainChanged', handleChainChanged);

		// Reload the page when they change networks
		function handleChainChanged(_chainId) {
			window.location.reload();
		}
	};

	const mintDomain = async () => {
		// Don't run if the domain is empty
		if (!domain) { return }
		// Alert the user if the domain is too short
		if (domain.length < 3) {
			alert('Domain must be at least 3 characters long');
			return;
		}
		// Calculate price based on length of domain (change this to match your contract)	
		// 3 chars = 0.05 BNB, 4 chars = 0.03 BNB, 5 or more = 0.01 BNB
		const price = domain.length === 3 ? '0.05' : domain.length === 4 ? '0.03' : '0.01';
		console.log("Minting domain", domain, "with price", price);
		try {
			const { ethereum } = window;
			if (ethereum) {
				const provider = new ethers.providers.Web3Provider(ethereum);
				const signer = provider.getSigner();
				const contract = new ethers.Contract(CONTRACT_ADDRESS, contractAbi.abi, signer);

				console.log("Going to pop wallet now to pay gas...")
				let tx = await contract.register(domain, { value: ethers.utils.parseEther(price) });
				// Wait for the transaction to be mined
				const receipt = await tx.wait();

				// Check if the transaction was successfully completed
				if (receipt.status === 1) {
					console.log("Domain minted! https://bscscan.com/tx/" + tx.hash);

					// Set the record for the domain
					tx = await contract.setRecord(domain, record);
					await tx.wait();

					console.log("Records set! https://bscscan.com/tx/" + tx.hash);

					// Call fetchMints after 2 seconds
					setTimeout(() => {
						fetchMints();
					}, 2000);

					setRecord('');
					setDomain('');
				}
				else {
					alert("Transaction failed! Please try again");
				}
			}
		}
		catch (error) {
			console.log(error);
		}
	}

	// Add this function anywhere in your component (maybe after the mint function)
	const fetchMints = async () => {
		try {
			const { ethereum } = window;
			if (ethereum) {
				// You know all this
				const provider = new ethers.providers.Web3Provider(ethereum);
				const signer = provider.getSigner();
				const contract = new ethers.Contract(CONTRACT_ADDRESS, contractAbi.abi, signer);

				// Get all the domain names from our contract
				const names = await contract.getAllNames();

				// For each name, get the record and the address
				const mintRecords = await Promise.all(names.map(async (name) => {
					const mintRecord = await contract.records(name);
					const owner = await contract.domains(name);
					return {
						id: names.indexOf(name),
						name: name,
						record: mintRecord,
						owner: owner,
					};
				}));

				console.log("MINTS FETCHED ", mintRecords);
				setMints(mintRecords);
			}
		} catch (error) {
			console.log(error);
		}
	}

	// This will run any time currentAccount or network are changed
	useEffect(() => {
		if (network === 'BSC Testnet') {
			fetchMints();
		}
	}, [currentAccount, network]);

	const updateDomain = async () => {
		if (!record || !domain) { return }
		setLoading(true);
		console.log("Updating domain", domain, "with record", record);
		try {
			const { ethereum } = window;
			if (ethereum) {
				const provider = new ethers.providers.Web3Provider(ethereum);
				const signer = provider.getSigner();
				const contract = new ethers.Contract(CONTRACT_ADDRESS, contractAbi.abi, signer);

				let tx = await contract.setRecord(domain, record);
				await tx.wait();
				console.log("Record set https://bscscan.com/tx/" + tx.hash);

				fetchMints();
				setRecord('');
				setDomain('');
			}
		} catch (error) {
			console.log(error);
		}
		setLoading(false);
	}

	// Render Methods
	const renderNotConnectedContainer = () => (
		<div className="connect-wallet-container">
			<img src="https://media.giphy.com/media/3ohhwytHcusSCXXOUg/giphy.gif" alt="Ninja donut gif" />
			{/* Call the connectWallet function we just wrote when the button is clicked */}
			<button onClick={connectWallet} className="cta-button connect-wallet-button">
				Connect Wallet
			</button>
		</div>
	);

	// Form to enter domain name and data
	const renderInputForm = () => {
		// If not on BSC Testnet, render "Please connect to BSC Testnet"
		if (network !== 'BSC Testnet') {
			return (
				<div className="connect-wallet-container">
					<img src="https://media.giphy.com/media/5mw9cfSZUGvhUFan4z/giphy.gif" alt="BSC Name Service" />
					{/* This button will call our switch network function */}
					<button className='cta-button mint-button' onClick={switchNetwork}>Switch to BSC Testnet</button>
				</div>
			);
		}

		// The rest of the function remains the same
		return (
			<Box>
				<Input sx={{ mt: 3 }} fullWidth
					value={domain}
					placeholder="domain"
					onChange={e => setDomain(e.target.value)}
					endAdornment={<InputAdornment position="end">{tld}</InputAdornment>}
				/>

				<Input sx={{ mt: 3 }} fullWidth
					type="text"
					value={record}
					placeholder="What do you mind ?"
					onChange={e => setRecord(e.target.value)}
				/>

				{editing ? (
					<Stack sx={{ mt: 3, mb: 3 }} direction="row" spacing={2}>
						<ColorButton variant="contained" disabled={loading} onClick={updateDomain}>
							Set record
						</ColorButton>
						<ColorButton variant="contained" onClick={() => { setEditing(false) }}>
							Cancel
						</ColorButton>
					</Stack>
				) : (

					<ColorButton fullWidth={true} sx={{ mt: 3, mb: 3 }} variant="contained" disabled={loading} onClick={mintDomain}>
						Mint
					</ColorButton>
				)}
			</Box>
		);
	}

	// Add this render function next to your other render functions
	const renderMints = () => {
		if (currentAccount && mints.length > 0) {
			return (
				<TableContainer component={Paper} sx={{ pt: 2, pb: 3 }}>
					<Table sx={{ minWidth: 650 }} aria-label="Domain Claimed">
						<TableHead>
							<TableRow>
								<TableCell>Domain Name</TableCell>
								<TableCell>Record</TableCell>
								<TableCell>Edit Record</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{mints.map((mint, index) => {
								return (
									<TableRow key={index}>
										<TableCell component="th" scope="row">
											{' '}{mint.name}{tld}{' '}
										</TableCell>
										<TableCell>{mint.record}</TableCell>
										<TableCell>
											{mint.owner.toLowerCase() === currentAccount.toLowerCase() ?
												<ColorButton variant="contained" onClick={() => editRecord(mint.name)}>
													Edit Record
												</ColorButton>
												:
												null
											}
										</TableCell>
									</TableRow>)
							})}
						</TableBody>
					</Table>
				</TableContainer>
			);
		}
	};

	// This will take us into edit mode and show us the edit buttons!
	const editRecord = (name) => {
		console.log("Editing record for", name);
		setEditing(true);
		setDomain(name);
	}

	useEffect(() => {
		checkIfWalletIsConnected();
	}, []);

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />

			<Box>
				<AppBar position="static">
					<Container>
						<Toolbar>
							<Typography variant="body1" sx={{ flexGrow: 1, mt: 1 }}>
								<img width="120" src="/theme/bsc-logo.png" alt="BSC Name Service" />
							</Typography>
							<Chip
								avatar={<Avatar alt="Network logo" src={network.includes("BSC Testnet") ? bscLogo : ethLogo} />}
								label={currentAccount ? <Typography variant="body1"> {currentAccount.slice(0, 6)}...{currentAccount.slice(-4)} </Typography> : <Typography variant="body1"> Not connected </Typography>}
								variant="outlined"
							/>
						</Toolbar>
					</Container>
				</AppBar>
			</Box>
			<Container sx={{ mt: 5 }}>
				{!currentAccount && renderNotConnectedContainer()}
			</Container>
			<Container sx={{ mt: 3 }}>
				{currentAccount && renderInputForm()}
			</Container>
			<Container sx={{ mt: 3 }}>
				{mints && renderMints()}
			</Container>
			<Container sx={{ mt: 3, mb: 5 }}>
				<Faq />
			</Container>
			<Footer />
		</ThemeProvider>
	);
}

export default App;